import { useSearch, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Flex, VStack, HStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { FaSpinner } from "react-icons/fa6";
import { useMcpAuthorizeDetails } from "../../../modules/mcp/api/useMcpAuthorizeDetails";
import { useMcpAuthorizeGrant } from "../../../modules/mcp/api/useMcpAuthorizeGrant";
import logo from "../../../assets/images/logo/Trellis_Logo.png";

function useOAuthParams() {
  const search = useSearch({ strict: false });
  return {
    client_id: search.client_id,
    response_type: search.response_type,
    redirect_uri: search.redirect_uri,
    state: search.state,
    code_challenge: search.code_challenge,
    code_challenge_method: search.code_challenge_method,
  };
}

function McpAuthorizePage() {
  const { t } = useTranslation(["mcp"]);
  const oauthParams = useOAuthParams();
  const details = useMcpAuthorizeDetails(oauthParams);
  const grant = useMcpAuthorizeGrant(oauthParams);

  const isSubmitting = grant.isPending || grant.isSuccess;

  if (details.isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <FaSpinner className={css({ color: "primary", fontSize: "xxlarge", animation: "spin 1s linear infinite" })} />
      </Flex>
    );
  }

  if (details.isError || !details.data) {
    const message =
      details.error instanceof Error ?
        details.error.message
      : t(($) => $.mcp.authorize["Failed to load authorization details."]);
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" padding="large">
        <VStack gap="medium" alignItems="center" maxWidth="500px">
          <div className={css({ fontSize: "large", fontWeight: "bold", color: "error" })}>
            {t(($) => $.mcp.authorize["Authorization Error"])}
          </div>
          <div className={css({ textAlign: "center", color: "gray.700" })}>{message}</div>
        </VStack>
      </Flex>
    );
  }

  const { app_name } = details.data;

  return (
    <Flex justifyContent="center" alignItems="center" height="100vh" padding="large">
      <VStack gap="large" alignItems="center" maxWidth="500px" width="100%">
        <img src={logo} alt={t(($) => $.mcp.authorize["Trellis"])} className={css({ height: "40px" })} />

        <div className={css({ fontSize: "xxlarge", fontWeight: "bold", textAlign: "center" })}>
          {t(($) => $.mcp.authorize["{appName} is requesting access"], { appName: app_name })}
        </div>

        {grant.isError && (
          <div className={css({ color: "error", textAlign: "center" })}>
            {grant.error instanceof Error ?
              grant.error.message
            : t(($) => $.mcp.authorize["Authorization failed. Please try again."])}
          </div>
        )}

        <HStack gap="medium" width="100%" justifyContent="center">
          <Button variant="secondary" isDisabled={isSubmitting} onPress={() => grant.mutate(false)}>
            {t(($) => $.mcp.authorize["Deny"])}
          </Button>
          <Button variant="primary" isDisabled={isSubmitting} onPress={() => grant.mutate(true)}>
            {isSubmitting ? t(($) => $.mcp.authorize["Redirecting..."]) : t(($) => $.mcp.authorize["Allow"])}
          </Button>
        </HStack>
      </VStack>
    </Flex>
  );
}

export const Route = createFileRoute("/_authenticated/mcp/authorize")({
  component: McpAuthorizePage,
});
